/**
 * Editor.js inline toolbar entries that delegate to React (color + comment overlays).
 * Handlers are read from a ref updated each render so the editor instance stays stable.
 */

/* Classic “artist palette” (Lucide-style): blob + paint dots — reads clearly as palette, not a droplet */
const PALETTE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="13.5" cy="6.5" r=".5" fill="currentColor" stroke="none"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor" stroke="none"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor" stroke="none"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor" stroke="none"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.65-.75 1.65-1.69 0-.43-.18-.83-.43-1.12-.29-.29-.44-.65-.44-1.13A1.64 1.64 0 0 1 14.5 16.5h2c3.05 0 5.55-2.5 5.55-5.55C22 6.01 17.46 2 12 2z"/></svg>';

const COMMENT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';

/**
 * @param {() => {{ onOpenColor?: (range: Range) => void }}} getHandlers
 */
export function createTextColorInlineTool(getHandlers) {
  return class TextColorInlineTool {
    static get isInline() {
      return true;
    }

    /** Merged into block HTML sanitizer so colored spans survive save */
    static get sanitize() {
      return {
        span: { style: true, class: true, 'data-doc-comment-id': true },
        font: { color: true, face: true, size: true },
      };
    }

    constructor({ api }) {
      this.api = api;
      this.button = null;
    }

    render() {
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.classList.add(this.api.styles.inlineToolButton);
      this.button.innerHTML = PALETTE_SVG;
      this.button.title = 'Text color';
      this.button.setAttribute('data-cy', 'inline-text-color');
      return this.button;
    }

    surround(range) {
      getHandlers().onOpenColor?.(range);
    }

    checkState() {
      return false;
    }
  };
}

/**
 * @param {() => {{ onOpenComment?: (range: Range) => void }}} getHandlers
 */
export function createInlineCommentTool(getHandlers) {
  return class InlineCommentTool {
    static get isInline() {
      return true;
    }

    static get sanitize() {
      return {
        span: { style: true, class: true, 'data-doc-comment-id': true },
      };
    }

    constructor({ api }) {
      this.api = api;
      this.button = null;
    }

    render() {
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.classList.add(this.api.styles.inlineToolButton);
      this.button.innerHTML = COMMENT_SVG;
      this.button.title = 'Comment';
      this.button.setAttribute('data-cy', 'inline-comment');
      return this.button;
    }

    surround(range) {
      getHandlers().onOpenComment?.(range);
    }

    checkState() {
      return false;
    }
  };
}
